import ErrorBoundary from '@components/Errors/ErrorBoundary';
import { MiniCartLiveRegion } from '@components/MiniCart/MiniCartLiveRegion';
import {
  formatMiniCartCount,
  formatMiniCartTriggerLabel,
  MINI_CART_MICROCOPY_DEFAULTS,
  MINI_CART_MICROCOPY_SET,
} from '@components/MiniCart/miniCartMicrocopy';
import { useMiniCartAnnouncement } from '@components/MiniCart/useMiniCartAnnouncement';
import { CartContext, CartProvider } from '@contexts/CartContext';
import { analytics } from '@lib/analytics/page-events';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import {
  subscribeToMiniCartOpenRequest,
  type MiniCartReturnFocus,
} from '@lib/commerce/miniCartOpen';
import {
  useHostCartRead,
  useReleaseCartRead,
} from '@lib/commerce/sharedCartLines';
import { useMultiCart } from '@lib/flags/useMultiCart';
import { HeaderFastActionItem } from '@lib/types/cms/Header';
import { c } from '@ui-components/utils/tailwind-merge';
import dynamic from 'next/dynamic';
import {
  createContext,
  ForwardedRef,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IconWithCount } from 'ui-components';
import { IconMenuOption } from '../Header/IconMenuOption';

// next/dynamic's loading slot takes no props, so the placeholder comes by context.
const MiniCartPlaceholderContext = createContext<ReactNode>(null);

const MiniCartDialogSlot = () => <>{useContext(MiniCartPlaceholderContext)}</>;

/**
 * The island's badge reads the cart, so the PDP shares that read. Rendered
 * inside the island's error boundary: an island that fails to load hands the
 * read back to the page.
 */
const MiniCartReadHost = () => {
  useHostCartRead();
  return null;
};

const MiniCartReadRelease = () => {
  useReleaseCartRead();
  return null;
};

/** Client-only, and on every MULTI_CART page: its badge needs the cart. */
const MiniCartDialogIsland = dynamic(
  () =>
    import('@components/MiniCart/MiniCartDialogIsland').then(
      ({ MiniCartDialogIsland }) => MiniCartDialogIsland
    ),
  { ssr: false, loading: MiniCartDialogSlot }
);

type FastActionMenuCartProps = {
  item: HeaderFastActionItem;
  forwardedRef: ForwardedRef<HTMLDivElement>;
  referenceProps: {};
  /** Merged onto the cart link's own classes. */
  className?: string;
};

type FastActionMenuCartLinkProps = FastActionMenuCartProps & {
  /** `undefined` hides the badge and leaves the count out of the label. */
  itemCount: number | undefined;
  /** Renders a button instead of the cart link. */
  onClick?: () => void;
  /** Set when the button opens the mini-cart. */
  isExpanded?: boolean;
  /** Localized name; the ASv2 cart keeps its English one. */
  ariaLabel?: string;
};

const FastActionMenuCartLink = ({
  item,
  forwardedRef,
  referenceProps,
  className,
  itemCount,
  onClick,
  isExpanded,
  ariaLabel,
}: FastActionMenuCartLinkProps) => {
  // TODO: Translate the text
  const cartAriaLabel =
    ariaLabel ??
    (itemCount === undefined
      ? `View ${item.topLevelLink?.value}`
      : `View ${item.topLevelLink?.value} (${itemCount} item${itemCount === 1 ? '' : 's'})`);
  return (
    <IconMenuOption
      name={cartAriaLabel}
      href={item.topLevelLink?.url || ''}
      ref={forwardedRef}
      {...referenceProps}
      {...(isExpanded !== undefined
        ? { 'aria-haspopup': 'dialog', 'aria-expanded': isExpanded }
        : {})}
      onClick={onClick}
      testId={isExpanded !== undefined ? 'mini-cart-trigger' : undefined}
      className={c('relative rounded-full', className)}
      target={item.topLevelLink?.openInNewTab ? '_blank' : undefined}
    >
      <IconWithCount
        iconName={item.topLevelLink?.icon || ''}
        iconSize={20}
        count={itemCount ?? 0}
      />
    </IconMenuOption>
  );
};

/** Today's ASv2 count, unchanged while MULTI_CART is off. */
const Asv2FastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { cart } = useContext(CartContext);
  return <FastActionMenuCartLink {...props} itemCount={cart?.ItemCount || 0} />;
};

/** CPS-7696: open state lives here so the PDP's requests (CPS-7695) work before the island loads. */
const MultiCartFastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { forwardedRef, referenceProps, className } = props;
  const [isOpen, setIsOpen] = useState(false);
  // Reported by the island, for the trigger's name and the live region.
  const [count, setCount] = useState<number>();
  const [hasNoCart, setHasNoCart] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const returnFocusRef = useRef<MiniCartReturnFocus | null>(null);
  const mc = useMicrocopy(
    MINI_CART_MICROCOPY_SET,
    MINI_CART_MICROCOPY_DEFAULTS
  );
  // A guest's first add goes from no cart to one item: news, not loading.
  const announcement = useMiniCartAnnouncement(
    count ?? (hasNoCart ? 0 : undefined),
    (changed) =>
      formatMiniCartCount(mc, 'mini-cart.count-announcement', {
        count: changed,
      })
  );
  const handleCountChange = useCallback(
    (changed: number | undefined, changedHasNoCart: boolean) => {
      setCount(changed);
      setHasNoCart(changedHasNoCart);
    },
    []
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    analytics.pushMiniCartActionEvent({
      mini_cart_action: open ? 'open' : 'close',
    });
    // MUI restores focus to what had it on open: <body> after the PDP's add.
    if (!open && returnFocusRef.current === 'cart-button') {
      setTimeout(() => triggerRef.current?.querySelector('button')?.focus());
    }
    returnFocusRef.current = null;
  }, []);

  useEffect(
    () =>
      subscribeToMiniCartOpenRequest(({ returnFocus }) => {
        handleOpenChange(true);
        returnFocusRef.current = returnFocus;
      }),
    [handleOpenChange]
  );

  const linkProps = { ...props, forwardedRef: null, referenceProps: {} };
  const label = props.item.topLevelLink?.value ?? '';
  const uncountedLabel = formatMiniCartTriggerLabel(mc, label, undefined);
  // Next caches a rejected chunk; the ASv2 cart link is not the commerce cart.
  const reloadPage = () => window.location.reload();
  const placeholder = (
    <FastActionMenuCartLink
      {...linkProps}
      itemCount={undefined}
      onClick={() => handleOpenChange(true)}
      isExpanded={isOpen}
      ariaLabel={uncountedLabel}
    />
  );

  return (
    <div
      ref={forwardedRef}
      {...referenceProps}
      className={c('flex items-center', className)}
    >
      <span ref={triggerRef} className="contents">
        <ErrorBoundary
          fallback={
            <>
              <MiniCartReadRelease />
              <FastActionMenuCartLink
                {...linkProps}
                itemCount={undefined}
                onClick={reloadPage}
                ariaLabel={uncountedLabel}
              />
            </>
          }
        >
          <MiniCartReadHost />
          <MiniCartPlaceholderContext.Provider value={placeholder}>
            <MiniCartDialogIsland
              open={isOpen}
              onOpenChange={handleOpenChange}
              onCountChange={handleCountChange}
              triggerLabel={formatMiniCartTriggerLabel(mc, label, count)}
              announcement={announcement}
              placeholder={placeholder}
            />
          </MiniCartPlaceholderContext.Provider>
        </ErrorBoundary>
      </span>
      {/* The island repeats it in the popover, as MUI hides the page then. */}
      {!isOpen && <MiniCartLiveRegion announcement={announcement} />}
    </div>
  );
};

export const FastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { enabled } = useMultiCart();
  return enabled ? (
    <MultiCartFastActionMenuCart {...props} />
  ) : (
    <Asv2FastActionMenuCart {...props} />
  );
};

/**
 * Mounts the legacy ASv2 CartProvider only while MULTI_CART is off: its
 * `/api/carts` read would otherwise still run (and mint a device for a
 * cookieless guest) even though the badge no longer reads it.
 */
export const FastActionMenuCartProvider = ({ children }: PropsWithChildren) => {
  const { enabled } = useMultiCart();
  return enabled ? <>{children}</> : <CartProvider>{children}</CartProvider>;
};
